import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import NProgress from "nprogress";
import "nprogress/nprogress.css";

// Global Layout Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTopButton from "./components/ScrollToTopButton";
import BannerSlider from "./components/Bannerslidefront";

// Main Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Aboutus from "./pages/about/about-section";
import Services from "./pages/Services";
import ServicesSection from "./pages/services/services-section";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import Portfolio from "./pages/Portfolio";
import Pricing from "./pages/Pricing";
import HomePricing from "./pages/home/home-pricing";
import Meeting from "./pages/Meeting";
import Chat from "./pages/Chat";
import Enquirynow from "./pages/Enquirynow";

// Plan Application Pages
import PackageApply from "./pages/apply/PackageApply";
import WebPlanApply from "./pages/apply/WebPlanApply";
import AppPlanApply from "./pages/apply/AppPlanApply";

// Specific Service Detail Pages
import WebsiteDevelopmentService from "./pages/services/WebsiteDevelopmentService";
import AppDevelopmentService from "./pages/services/AppDevelopmentService";
import DigitalMarketingService from "./pages/services/DigitalMarketingService";
import EcommerceService from "./pages/services/EcommerceService";
import EnterpriseSoftwareService from "./pages/services/EnterpriseSoftwareService";
import UiUxDesignService from "./pages/services/UiUxDesignService";

// Product Routes
import OurProductsAll from "./pages/ourproducts/our-products-all";
import AppsProducts from "./pages/ourproducts/apps-products";
import WebsiteProducts from "./pages/ourproducts/website-products";
import SoftwareProducts from "./pages/ourproducts/software-products";
import CrmProDetail from "./pages/ourproducts/details/CrmProDetail";
import PosWoocommerceDetail from "./pages/ourproducts/details/PosWoocommerceDetail";
import StoreitDetail from "./pages/ourproducts/details/StoreitDetail";
import TracknnDetail from "./pages/ourproducts/details/TracknnDetail";
import MusicProDetail from "./pages/ourproducts/details/MusicProDetail";

// Home Sectional Routes & Tracker
import HomePortfolio from "./pages/home/home-portfolio";
import Homeportfolioscrolling from "./pages/home/portfolio-home-scrolling";
import Tracknn from "./pages/applink/Tracknn";

// Legal Pages
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Refundpolicy from "./pages/Refundpolicy";

// Custom NProgress Styles for Cyber Aesthetics
const customNProgressStyles = `
  #nprogress .bar {
    background: linear-gradient(
      90deg, 
      #3b82f6 0%, 
      #06b6d4 50%, 
      #8b5cf6 100%
    ) !important;
    background-size: 200% 100% !important;
    animation: nprogressGlow 0.8s ease infinite !important;
    height: 4px !important;
    z-index: 99999 !important;
    border-bottom-right-radius: 4px;
    border-top-right-radius: 4px;
  }

  #nprogress .peg {
    box-shadow: 0 0 15px #06b6d4, 0 0 8px #3b82f6 !important;
  }

  #nprogress .spinner {
    display: none !important;
  }

  @keyframes nprogressGlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

function InstantNavigationHandler() {
  const location = useLocation();

  useEffect(() => {
    NProgress.configure({ showSpinner: false, speed: 200, minimum: 0.1 });
    NProgress.start();
    NProgress.done();

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant" as ScrollBehavior
    });
  }, [location.pathname]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const currentPath = location.pathname.toLowerCase();

  // HIDE TOP NAVBAR ONLY ON ENQUIRYNOW & TRACKNN BY DEFAULT
  const hideNavbarPaths = ["/enquirynow", "/tracknn"];
  const shouldHideNavbar = hideNavbarPaths.includes(currentPath);

  const hideFooterPaths: string[] = [];
  const shouldHideFooter = shouldHideNavbar || hideFooterPaths.includes(currentPath);

  return (
    <>
      <style>{customNProgressStyles}</style>

      <InstantNavigationHandler />

      <div className="flex flex-col min-h-screen bg-[#030712] text-slate-200 antialiased font-body">
        {!shouldHideNavbar && <Navbar />}
        {!shouldHideNavbar && <ScrollToTopButton />}

        <main className="flex-grow pt-0">
          <Routes>
            {/* Standard Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/about-section" element={<Aboutus />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/enquirynow" element={<Enquirynow />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services-section" element={<ServicesSection />} />

            {/* Specific Service Detail Routes */}
            <Route path="/services/website-development" element={<WebsiteDevelopmentService />} />
            <Route path="/services/app-development" element={<AppDevelopmentService />} />
            <Route path="/services/digital-marketing-seo" element={<DigitalMarketingService />} />
            <Route path="/services/ecommerce-solutions" element={<EcommerceService />} />
            <Route path="/services/custom-enterprise-software" element={<EnterpriseSoftwareService />} />
            <Route path="/services/ui-ux-logo-design" element={<UiUxDesignService />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/Portfolio" element={<Portfolio />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/Pricing" element={<Pricing />} />
            <Route path="/Meeting" element={<Meeting />} />

            {/* Plan Application Dedicated Routes */}
            <Route path="/apply/package" element={<PackageApply />} />
            <Route path="/apply/website" element={<WebPlanApply />} />
            <Route path="/apply/app" element={<AppPlanApply />} />

            {/* Product Routes */}
            <Route path="/ourproducts/apps-products" element={<AppsProducts />} />
            <Route path="/ourproducts/our-products-all" element={<OurProductsAll />} />
            <Route path="/ourproducts/website-products" element={<WebsiteProducts />} />
            <Route path="/ourproducts/software-products" element={<SoftwareProducts />} />

            {/* Dedicated Product Detail Routes */}
            <Route path="/ourproducts/app/crmpro" element={<CrmProDetail />} />
            <Route path="/ourproducts/app/pos-woocommerce" element={<PosWoocommerceDetail />} />
            <Route path="/ourproducts/app/storeit" element={<StoreitDetail />} />
            <Route path="/ourproducts/app/tracknn" element={<TracknnDetail />} />
            <Route path="/ourproducts/app/musicpro" element={<MusicProDetail />} />

            {/* Home & Sectional Routes */}
            <Route path="/home-portfolio" element={<HomePortfolio />} />
            <Route path="/Homeportfolioscrolling" element={<Homeportfolioscrolling />} />
            <Route path="/home-pricing" element={<HomePricing />} />
            <Route path="/banner-slider" element={<BannerSlider />} />

            {/* Legal Routes */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/refund-policy" element={<Refundpolicy />} />
            <Route path="/applink/tracknn" element={<Tracknn />} />

            {/* Fallback Catch-All Redirect */}
            <Route path="*" element={<Home />} />
          </Routes>
        </main>

        {!shouldHideFooter && <Footer />}
      </div>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
